// src/encounter/services/use-ai-diagnosis-search.ts
// Flutter's AI "Suggested diagnosis" search — ranked ICD codes for the chief-complaint text, each row
// carrying a confidence percentage (PROD-1801 / A/P-25).
//
// TRACED, not guessed. FetchAIDiagnosisCodeUseCase (fetch_diagnosis_code_use_case.dart:35-45) →
// CommonRepository.aiDiagnosisList → common_data_source_impl.dart:1158-1162, which GETs
// `${masterAPI(MasterService.AI)}/Diagnosis?search=$query`. MasterService.AI resolves to `/api/AISearch`
// (scrips_core .../api_constants.dart:342-343). So the literal path is:
//
//     GET /api/AISearch/Diagnosis?search=<query>
//
// Row keys are PascalCase on the wire (ai_diagnosis_data.dart fromJson): Id · Code · Display ·
// Definition · Score · PercentageScore · RerankerScore. `PercentageScore` is the one the row renders
// as a bar; Score and RerankerScore are the raw retrieval numbers and are not shown.
//
// GATED BY DATA, never a tenant check: Flutter only offers this when
// `resourceBasedIntegration.diagnosisSystem == 'AzureAI'` (assessment_plan_search_list_widget.dart:
// 872-900). The caller passes `enabled` from that real config — this hook does not decide.
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';

/** One ranked suggestion. `percentageScore` is 0-100 as the backend sends it. */
export interface AiDiagnosisSuggestion {
  id: string;
  code: string;
  display: string;
  definition?: string;
  /** Confidence 0-100. Absent when the backend omits it — the row then shows no bar. */
  percentageScore?: number;
}

/** The wire row — PascalCase, matching Flutter's fromJson exactly. */
interface AiDiagnosisRow {
  Id?: string | null;
  Code?: string | null;
  Display?: string | null;
  Definition?: string | null;
  PercentageScore?: number | null;
}

function toSuggestion(row: AiDiagnosisRow): AiDiagnosisSuggestion | null {
  const code = row.Code?.trim() ?? '';
  const display = row.Display?.trim() ?? '';
  if (!code && !display) return null;
  const pct = typeof row.PercentageScore === 'number' ? row.PercentageScore : undefined;
  return {
    id: row.Id?.trim() || code || display,
    code: code || display,
    display: display || code,
    ...(row.Definition?.trim() ? { definition: row.Definition.trim() } : {}),
    // Clamp defensively: a bar driven by an out-of-range number renders past its track.
    ...(pct != null ? { percentageScore: Math.max(0, Math.min(100, pct)) } : {}),
  };
}

/**
 * Ranked AI diagnosis suggestions for a query.
 *
 * `enabled` carries the tenant gate AND the "has the clinician asked" state — Flutter fetches only on
 * an explicit Suggest tap, not on every keystroke (APSLW:872-900), so this is deliberately NOT wired
 * to the search field's onChange.
 */
export function useAiDiagnosisSearch(query: string, enabled: boolean) {
  const q = query.trim();
  return useQuery<AiDiagnosisSuggestion[]>({
    queryKey: ['ai-diagnosis-search', q],
    enabled: enabled && q.length > 0,
    queryFn: async ({ signal }) => {
      const rows = await get<AiDiagnosisRow[]>(
        `/api/AISearch/Diagnosis?search=${encodeURIComponent(q)}`,
        { signal },
      );
      return (rows ?? []).flatMap((r) => {
        const hit = toSuggestion(r);
        return hit ? [hit] : [];
      });
    },
  });
}

/** Exposed for tests — the mapper carries the PascalCase-key risk, so it is pinned directly. */
export const __testables = { toSuggestion };
